// campaign-setup-tabs.tsx — the campaign page's Audience and When tabs.
//
// They replace the "Edit scheduled campaign" popup: viewing a campaign and
// editing it now happen on the same page. Each tab shows the value read-only
// once it is locked (see campaign-setup-edit.ts for the rule and for how each
// campaign kind saves), and otherwise edits a local working copy with an
// explicit Save — never optimistically, because a standalone campaign's save is
// a server-side compare-and-set that can refuse.
//
// Styled with the page's own tokens (sequence-detail.module.scss) and the
// redesign's `.btn` / `.inp` utilities, so the tabs read as part of the page.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Lock, Repeat, Search, Users } from 'lucide-react';

import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { AUDIENCE_LIST_QUERY, DBBoardsThunks, boardsSelectByQuery } from '@common-reducers/DBServiceThunks';
import { getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import type { Board } from '@common-models/board';
import BTDatePicker from '@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-date-picker';
import BTTimePicker from '@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-time-picker';

import { sameAudienceIds } from './audience-selection';
import { formatDateTime } from './campaign-schedule';
import { recurrenceSummary } from './campaign-recurrence-summary';
import { fromDateTimeFields, toDateTimeFields, type DateTimeFields } from './scheduled-campaign-edit';
import {
  setupLockText,
  setupSaveOutcomeText,
  type SetupLock,
  type SetupSaveOutcome,
} from './campaign-setup-edit';
import styles from './sequence-detail.module.scss';

// ── shared pieces ────────────────────────────────────────────────────────────

const LockNotice: React.FC<{ text: string }> = ({ text }) => (
  <div className={styles.lockNotice}>
    <Lock size={14} />
    <span>{text}</span>
  </div>
);

const OutcomeLine: React.FC<{ outcome: SetupSaveOutcome | null }> = ({ outcome }) => {
  const { t } = useTranslation(namespace);
  if (!outcome) return null;
  const { tone, text } = setupSaveOutcomeText(outcome, t);
  return (
    <div className={`${styles.outcome} ${styles[`outcome_${tone}`]}`} role="status">
      {tone === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
      <span>{text}</span>
    </div>
  );
};

/** Appears only while there is something to save; sticks to the bottom of the
 *  scroll area so Save is never scrolled out of reach below a long list. */
const SaveBar: React.FC<{
  saving: boolean;
  disabledReason?: string;
  onDiscard: () => void;
  onSave: () => void;
}> = ({ saving, disabledReason, onDiscard, onSave }) => {
  const { t } = useTranslation(namespace);
  return (
    <div className={styles.saveBar}>
      <span className={styles.saveBarText}>
        {disabledReason || t('campaignsRedesign.setup.unsaved', 'You have unsaved changes')}
      </span>
      <button type="button" className="btn ghost" onClick={onDiscard} disabled={saving}>
        {t('campaignsRedesign.setup.discard', 'Discard')}
      </button>
      <button type="button" className="btn primary" onClick={onSave} disabled={saving || Boolean(disabledReason)}>
        {saving ? t('campaignsRedesign.editModal.saving', 'Saving…') : t('common.saveChanges', 'Save changes')}
      </button>
    </div>
  );
};

/** Run a save, holding the outcome for the tab to show. */
function useSave<A>(onSave: (arg: A) => Promise<SetupSaveOutcome>, onSettled: (outcome: SetupSaveOutcome) => void) {
  const [saving, setSaving] = React.useState(false);
  const [outcome, setOutcome] = React.useState<SetupSaveOutcome | null>(null);
  const run = async (arg: A) => {
    setSaving(true);
    setOutcome(null);
    const result = await onSave(arg).catch((): SetupSaveOutcome => ({ kind: 'error' }));
    setSaving(false);
    setOutcome(result);
    onSettled(result);
  };
  return { saving, outcome, setOutcome, run };
}

// ── Audience ─────────────────────────────────────────────────────────────────

interface CampaignAudienceTabProps {
  /** The audience the campaign targets now. */
  storedIds: string[];
  lock: SetupLock;
  onSave: (ids: string[]) => Promise<SetupSaveOutcome>;
}

export const CampaignAudienceTab: React.FC<CampaignAudienceTabProps> = ({ storedIds, lock, onSave }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    // ALL audiences: the server paginates at 10 otherwise, hiding the rest.
    dispatch(DBBoardsThunks.find({ type: 'audience', deleted: { $ne: true }, ...AUDIENCE_LIST_QUERY }));
  }, [dispatch]);

  const boards = useAppSelector((s) =>
    boardsSelectByQuery(s.DBBoardsReducer, { type: 'audience', deleted: { $ne: true } }),
  ) as Board[];
  const taskSum = useAppSelector(getTaskSumByBoardIdRecord);

  // `null` = untouched: the tab shows the stored audience, and follows it if it
  // changes underneath (an MQTT update) instead of pinning a stale copy.
  const [draft, setDraft] = React.useState<string[] | null>(null);
  const selected = draft ?? storedIds;
  const dirty = draft !== null && !sameAudienceIds(draft, storedIds);
  const [search, setSearch] = React.useState('');

  const { saving, outcome, setOutcome, run } = useSave(onSave, (result) => {
    // A refusal means the audience is frozen server-side — the working copy is
    // moot. A failed call keeps it, so the retry is one click.
    if (result.kind !== 'error') setDraft(null);
  });

  const toggle = (id: string) => {
    setOutcome(null);
    setDraft((prev) => {
      const base = prev ?? storedIds;
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
  };

  const countOf = (id: string) => taskSum[id] ?? 0;
  const total = selected.reduce((sum, id) => sum + countOf(id), 0);
  const nameOf = (id: string) => {
    const board = boards.find((b) => String(b._id) === id);
    return board ? (board.name || t('audiences.unnamed', 'Untitled audience')) : t('campaignsRedesign.setup.missingAudience', 'Deleted audience');
  };

  const summary = (
    <div className={styles.setupFooter}>
      <Users size={13} />
      {t('campaignsRedesign.setup.audienceSummary', {
        // Label form, not "N audiences": it reads right at 1 in every language
        // without a plural table per key.
        defaultValue: 'Audiences: {{audiences}} · Contacts: {{contacts}}',
        audiences: selected.length,
        contacts: total,
      })}
    </div>
  );

  if (lock) {
    return (
      <div className={styles.setupStack}>
        <LockNotice text={setupLockText(lock, 'audience', t)} />
        <section className={styles.setupCard}>
          <h2 className={styles.setupTitle}>{t('campaignsRedesign.setup.audienceTitle', 'Who receives this campaign')}</h2>
          <div className={styles.pickList}>
            {storedIds.map((id) => (
              <div key={id} className={`${styles.pickRow} ${styles.pickRowStatic}`}>
                <Users size={15} className={styles.pickIcon} />
                <span className={styles.pickName}>{nameOf(id)}</span>
                <span className={styles.pickCount}>{t('audiences.contactCount', '{{count}} contacts', { count: countOf(id) })}</span>
              </div>
            ))}
          </div>
          {summary}
        </section>
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  // The campaign's CURRENT audience first — among dozens it would otherwise sit
  // anywhere in the list. Keyed on the stored audience, not the working copy, so
  // rows do not jump while the user ticks and unticks.
  const visible = (q ? boards.filter((b) => (b.name ?? '').toLowerCase().includes(q)) : boards)
    .slice()
    .sort((a, b) => Number(storedIds.includes(String(b._id))) - Number(storedIds.includes(String(a._id))));

  return (
    <div className={styles.setupStack}>
      <section className={styles.setupCard}>
        <h2 className={styles.setupTitle}>{t('campaignsRedesign.setup.audienceTitle', 'Who receives this campaign')}</h2>
        <p className={styles.setupSub}>
          {t('campaignsRedesign.setup.audienceSub', 'Pick one or more audiences. You can change this until the first message is sent.')}
        </p>

        <div className={styles.setupSearch}>
          <Search size={14} className={styles.setupSearchIcon} />
          <input
            className="inp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('audiences.searchPlaceholder', 'Search audiences')}
            // Inline, as the Audiences tab does: `.campaigns-rd .inp` sets the
            // padding at the same specificity and would win over a module rule.
            style={{ paddingInlineStart: 32 }}
          />
        </div>

        <div className={styles.pickList}>
          {visible.length === 0 ? (
            <div className={styles.pickEmpty}>
              {q ? t('audiences.noResults', 'No audiences match your search') : t('campaignsRedesign.setup.noAudiences', 'No audiences yet')}
            </div>
          ) : visible.map((board) => {
            const id = String(board._id ?? '');
            const checked = selected.includes(id);
            return (
              <label key={id} className={`${styles.pickRow} ${checked ? styles.pickRowOn : ''}`}>
                <input
                  type="checkbox"
                  className={styles.pickCheck}
                  checked={checked}
                  onChange={() => toggle(id)}
                />
                <span className={styles.pickName}>{board.name || t('audiences.unnamed', 'Untitled audience')}</span>
                <span className={styles.pickCount}>{t('audiences.contactCount', '{{count}} contacts', { count: countOf(id) })}</span>
              </label>
            );
          })}
        </div>
        {summary}
        <OutcomeLine outcome={outcome} />
      </section>

      {dirty && (
        <SaveBar
          saving={saving}
          disabledReason={selected.length === 0 ? t('campaignsRedesign.scheduledEdit.blockerNoAudience', 'Choose at least one audience.') : undefined}
          onDiscard={() => { setDraft(null); setOutcome(null); }}
          onSave={() => { void run(selected); }}
        />
      )}
    </div>
  );
};

// ── When ─────────────────────────────────────────────────────────────────────

export interface ScheduleRow {
  key: string;
  label: string;
  /** "1 day after the previous message" — absent on the first message. */
  gapLabel?: string;
  at?: Date;
  state: 'done' | 'in-flight' | 'scheduled';
}

interface CampaignWhenTabProps {
  storedAt: Date | undefined;
  lock: SetupLock;
  /** When the first message actually went out (locked view). */
  startedAt?: Date;
  rruleset?: string;
  multi: boolean;
  /** Every message's send time, projected from a first-send time. */
  rowsFor: (firstSendAt: Date | undefined) => ScheduleRow[];
  onSave: (at: Date) => Promise<SetupSaveOutcome>;
  onOpenMessages: () => void;
}

export const CampaignWhenTab: React.FC<CampaignWhenTabProps> = ({
  storedAt,
  lock,
  startedAt,
  rruleset,
  multi,
  rowsFor,
  onSave,
  onOpenMessages,
}) => {
  const { t } = useTranslation(namespace);

  const [draft, setDraft] = React.useState<DateTimeFields | null>(null);
  const fields = draft ?? toDateTimeFields(storedAt);
  const picked = fromDateTimeFields(fields);
  const dirty = draft !== null && picked?.getTime() !== storedAt?.getTime();

  const { saving, outcome, setOutcome, run } = useSave(onSave, (result) => {
    if (result.kind !== 'error') setDraft(null);
  });

  const edit = (patch: Partial<DateTimeFields>) => {
    setOutcome(null);
    setDraft((prev) => ({ ...(prev ?? toDateTimeFields(storedAt)), ...patch }));
  };

  const repeats = rruleset ? recurrenceSummary(rruleset, t) : '';
  const rows = rowsFor(lock ? storedAt : (picked ?? storedAt));
  const inPast = !lock && dirty && picked && picked.getTime() < Date.now();

  const schedule = multi && rows.length > 0 && (
    <section className={styles.setupCard}>
      <h2 className={styles.setupTitle}>{t('campaignsRedesign.setup.scheduleTitle', 'Message schedule')}</h2>
      <ol className={styles.schedList}>
        {rows.map((row) => (
          <li key={row.key} className={styles.schedRow}>
            <span className={`${styles.schedDot} ${styles[`schedDot_${row.state}`]}`} aria-hidden="true" />
            <span className={styles.schedText}>
              <span className={styles.schedLabel}>{row.label}</span>
              {row.gapLabel && <span className={styles.schedGap}>{row.gapLabel}</span>}
            </span>
            <span className={styles.schedAt}>{row.at ? formatDateTime(row.at) : '—'}</span>
          </li>
        ))}
      </ol>
      <div className={styles.setupFooter}>
        <span>{t('campaignsRedesign.setup.gapsOnMessages', 'The waits between messages are changed on the Messages tab.')}</span>
        <button type="button" className={styles.linkBtn} onClick={onOpenMessages}>
          {t('campaignsRedesign.setup.openMessages', 'Open Messages')}
        </button>
      </div>
    </section>
  );

  if (lock) {
    const at = startedAt ?? storedAt;
    return (
      <div className={styles.setupStack}>
        <LockNotice text={setupLockText(lock, 'when', t)} />
        <section className={styles.setupCard}>
          {/* A statement, not the editor's question — nothing is being asked. */}
          <h2 className={styles.setupTitle}>{t('campaignsRedesign.setup.whenTitleLocked', 'Start time')}</h2>
          <div className={styles.whenValue}>
            {at
              ? (startedAt
                ? t('campaignsRedesign.startedAt', { defaultValue: 'Started {{when}}', when: formatDateTime(at) })
                : formatDateTime(at))
              : t('campaignsRedesign.startedUnknown', 'Start time unknown')}
          </div>
          {repeats && <div className={styles.whenRepeats}><Repeat size={13} /> {t('campaignsRedesign.wizard.repeats', 'Repeats {{summary}}', { summary: repeats })}</div>}
        </section>
        {schedule}
      </div>
    );
  }

  return (
    <div className={styles.setupStack}>
      <section className={styles.setupCard}>
        <h2 className={styles.setupTitle}>
          {multi
            ? t('campaignsRedesign.wizard.timingTitleMulti', 'When does the first message go out?')
            : t('campaignsRedesign.wizard.timingTitle', 'When should it send?')}
        </h2>
        <p className={styles.setupSub}>
          {t('campaignsRedesign.scheduledEdit.sendAtHint', 'Your local time. Messages are then paced out one by one from that moment.')}
        </p>
        <div className={styles.whenFields}>
          <BTDatePicker value={fields.date} onChange={(date) => edit({ date })} />
          <BTTimePicker value={fields.time} onChange={(time) => edit({ time })} />
        </div>
        {inPast && (
          <div className={styles.warnLine}>
            <AlertTriangle size={13} />
            {t('campaignsRedesign.setup.pastTime', 'That time has already passed — sending will start right away.')}
          </div>
        )}
        {repeats && <div className={styles.whenRepeats}><Repeat size={13} /> {t('campaignsRedesign.wizard.repeats', 'Repeats {{summary}}', { summary: repeats })}</div>}
        <OutcomeLine outcome={outcome} />
      </section>

      {schedule}

      {dirty && (
        <SaveBar
          saving={saving}
          disabledReason={picked ? undefined : t('campaignsRedesign.scheduledEdit.blockerInvalidTime', 'Choose a date and time to send.')}
          onDiscard={() => { setDraft(null); setOutcome(null); }}
          onSave={() => { if (picked) void run(picked); }}
        />
      )}
    </div>
  );
};
